import { Component } from '@angular/core';

/**
 * WhatsApp button.
 *
 * Opens a WhatsApp chat with the CX number. Rendered from the enterprise layout header so
 * it sits inside the banner, top right, on every screen of the PWA and of the packaged
 * iOS / Android apps. The header is a flex row, so it can never collide with the Login
 * pill that shares that corner.
 */
@Component({
    selector  : 'app-whatsapp-button',
    standalone: true,
    template  : `
        <a class="wa"
           [href]="chatUrl"
           target="_blank"
           rel="noopener noreferrer"
           [title]="'Chat to us on WhatsApp - ' + displayNumber"
           [attr.aria-label]="'Chat to Infini-fi on WhatsApp, 24/7 on ' + displayNumber">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
            </svg>
        </a>
    `,
    styles: [`
        :host {
            display: flex;
            align-items: center;
        }

        .wa {
            position: relative;
            flex: none;
            display: flex;
            align-items: center;
            justify-content: center;
            /* Sized to sit inside the banner, which is min-h-[48px] and grows at sm / md. */
            width: 2.25rem;
            height: 2.25rem;
            border-radius: 9999px;
            /* WhatsApp green sampled off the SADV badge. */
            background-color: #009e61;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
            transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        /* Widens the tap target to ~44px without making the circle look bigger. */
        .wa::after {
            content: '';
            position: absolute;
            inset: -0.25rem;
            border-radius: inherit;
        }

        @media (min-width: 640px) {
            .wa {
                width: 2.5rem;
                height: 2.5rem;
            }
        }

        @media (min-width: 768px) {
            .wa {
                width: 2.75rem;
                height: 2.75rem;
            }
        }

        .wa svg {
            width: 60%;
            height: 60%;
            fill: #ffffff;
        }

        .wa:hover {
            transform: scale(1.06);
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.32);
        }

        .wa:active {
            transform: scale(0.95);
        }

        .wa:focus-visible {
            outline: 3px solid #fbbf24;
            outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
            .wa {
                transition: none;
            }

            .wa:hover,
            .wa:active {
                transform: none;
            }
        }

        @media print {
            .wa {
                display: none;
            }
        }
    `],
})
export class WhatsappButtonComponent
{
    /** CX WhatsApp number (084 555 8858) in the international format wa.me requires. */
    private static readonly CX_NUMBER = '27845558858';

    /** Same number as chatUrl, spaced the way it is read out to customers. */
    public readonly displayNumber = '+27 84 555 8858';

    public readonly chatUrl = `https://wa.me/${WhatsappButtonComponent.CX_NUMBER}`
        + `?text=${encodeURIComponent('Hi Infini-fi, please can you assist me')}`;
}
